import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SkillCategory = ({ category, skills, isActive, onToggle }) => {
  const [hoveredSkill, setHoveredSkill] = useState(null);

  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      'Frontend': 'Monitor',
      'Backend': 'Server',
      'Database': 'Database',
      'DevOps': 'Cloud',
      'Tools': 'Wrench',
      'Mobile': 'Smartphone'
    };
    return iconMap[categoryName] || 'Code';
  };

  const getProficiencyColor = (level) => {
    if (level >= 90) return 'bg-success';
    if (level >= 75) return 'bg-primary';
    if (level >= 60) return 'bg-accent';
    return 'bg-secondary';
  };

  const getProficiencyLabel = (level) => {
    if (level >= 90) return 'Expert';
    if (level >= 75) return 'Advanced';
    if (level >= 60) return 'Intermediate';
    return 'Beginner';
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-premium transition-premium hover:shadow-lg">
      <button
        onClick={onToggle}
        className="w-full p-6 text-left flex items-center justify-between hover:bg-surface-hover rounded-t-xl transition-premium"
      >
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-primary to-brand-purple rounded-lg flex items-center justify-center">
            <Icon name={getCategoryIcon(category)} size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-text-primary">{category}</h3>
            <p className="text-sm text-text-secondary">{skills.length} technologies</p>
          </div>
        </div>
        <Icon 
          name={isActive ? 'ChevronUp' : 'ChevronDown'} 
          size={20} 
          className="text-text-secondary transition-transform duration-300"
        />
      </button>

      <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
        isActive ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="p-6 pt-0 grid grid-cols-1 md:grid-cols-2 gap-4">
          {skills.map((skill, index) => (
            <div
              key={skill.name}
              className="relative group"
              onMouseEnter={() => setHoveredSkill(skill.name)}
              onMouseLeave={() => setHoveredSkill(null)}
            >
              <div className="p-4 bg-surface rounded-lg border border-border hover:border-primary/30 transition-premium hover-lift">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      <span className="text-xs font-bold text-primary">{skill.name.charAt(0)}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-text-primary">{skill.name}</h4>
                      <p className="text-xs text-text-secondary">{skill.experience} years</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-text-primary">{skill.proficiency}%</div>
                    <div className={`text-xs px-2 py-1 rounded-full text-white ${getProficiencyColor(skill.proficiency)}`}>
                      {getProficiencyLabel(skill.proficiency)}
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="w-full bg-border rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-1000 ease-out ${getProficiencyColor(skill.proficiency)}`}
                      style={{ 
                        width: isActive ? `${skill.proficiency}%` : '0%',
                        transitionDelay: `${index * 100}ms`
                      }}
                    />
                  </div>
                </div>

                {/* Recent Projects */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {skill.recentProjects.map((project, idx) => (
                    <span
                      key={idx}
                      className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full border border-primary/20"
                    >
                      {project}
                    </span>
                  ))}
                </div>

                {/* Hover Details */}
                {hoveredSkill === skill.name && (
                  <div className="absolute top-full left-0 right-0 mt-2 p-4 bg-white rounded-lg shadow-xl border border-border z-10 transform transition-all duration-200">
                    <div className="space-y-2">
                      <div>
                        <h5 className="font-medium text-text-primary mb-1">Key Strengths:</h5>
                        <ul className="text-sm text-text-secondary space-y-1">
                          {skill.keyStrengths.map((strength, idx) => (
                            <li key={idx} className="flex items-center space-x-2">
                              <Icon name="Check" size={12} className="text-success" />
                              <span>{strength}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {skill.codeSnippet && (
                        <div>
                          <h5 className="font-medium text-text-primary mb-1">Code Sample:</h5>
                          <pre className="text-xs bg-surface p-2 rounded border font-mono overflow-x-auto">
                            <code>{skill.codeSnippet}</code>
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SkillCategory;